<template>
  <div id="payment-groups-grid-page">
    <data-view-sidebar
      :isSidebarActive="addNewDataSidebar"
      @closeSidebar="toggleDataSidebar"
      :data="sidebarData"
    />

    <!-- Filters -->
    <vx-card
      ref="filterCard"
      title="Filtre"
      class="bank-list-filters mb-8"
      actionButtons
      @refresh="resetFilters"
    >
      <div class="vx-row">
        <div class="vx-col md:w-1/2 sm:w-1/2 w-full">
          <label class="text-sm opacity-75">Durum</label>
          <v-select
            :options="statusOptions"
            :clearable="false"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
            v-model="statusFilter"
            class="mb-4 md:mb-0"
          />
        </div>
        <div class="vx-col md:w-1/2 sm:w-1/2 w-full">
          <label class="text-sm opacity-75">Arama</label>
          <vs-input
            class="w-full"
            v-model="searchQuery"
            @input="debouncedSearch"
            :placeholder="$t('GridSearch')"
          />
        </div>
      </div>
    </vx-card>

    <!-- Data Card -->
    <div class="vx-card p-6">
      <div class="flex flex-wrap items-center">
        <div class="flex-grow">
          <vs-dropdown vs-trigger-click class="cursor-pointer">
            <div
              class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
            >
              <span class="mr-2"
                >{{ paginationStart }} - {{ paginationEnd }} /
                {{ filteredData.length }}</span
              >
              <feather-icon
                icon="ChevronDownIcon"
                svgClasses="h-4 w-4"
              />
            </div>
            <vs-dropdown-menu>
              <vs-dropdown-item @click="pageSize = 10">
                <span>10</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="pageSize = 20">
                <span>20</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="pageSize = 50">
                <span>50</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="pageSize = 100">
                <span>100</span>
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
        </div>

        <vs-button
          class="mb-4 md:mb-0 ml-4"
          color="success"
          type="border"
          icon-pack="feather"
          icon="icon-plus"
          @click="addNewData"
          >Yeni Hizmet Ekle</vs-button
        >
      </div>

      <ag-grid-vue
        ref="agGridTable"
        id="paymentGroupsGrid"
        :gridOptions="gridOptions"
        class="ag-theme-material w-100 my-4 ag-grid-table vs-con-loading__container"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="filteredData"
        :components="components"
        :context="{ componentParent: this }"
        rowSelection="single"
        :animateRows="true"
        :pagination="true"
        :paginationPageSize="pageSize"
        :suppressPaginationPanel="true"
        @grid-ready="onGridReady"
      >
      </ag-grid-vue>

      <div class="flex flex-wrap justify-between items-center">
        <span class="text-sm text-grey">
          Toplam {{ filteredData.length }} kayıt, Sayfa {{ currentPage }} /
          {{ totalPages }}
        </span>
        <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
      </div>
    </div>

    <!-- Detail Popup -->
    <vs-popup
      v-show="detailPopup"
      :title="detailTitle"
      fullscreen
      :active.sync="detailPopup"
    >
      <div v-if="selectedProduct" class="p-6">
        <div class="vx-row">
          <div class="vx-col w-full md:w-1/2">
            <h4 class="mb-4">Hizmet Bilgileri</h4>
            <table class="w-full">
              <tr class="border-b">
                <td class="py-2 font-semibold">Hizmet Adı</td>
                <td class="py-2">{{ selectedProduct.ProductTitle }}</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 font-semibold">Açıklama</td>
                <td class="py-2">{{ selectedProduct.ProductDesc }}</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 font-semibold">Fiyat</td>
                <td class="py-2">{{ selectedProduct.Price | currency }}</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 font-semibold">Durum</td>
                <td class="py-2">
                  <vs-chip :color="selectedProduct.IsActive && !selectedProduct.IsDeleted ? 'success' : 'danger'">
                    {{ selectedProduct.IsActive && !selectedProduct.IsDeleted ? 'Aktif' : 'Pasif' }}
                  </vs-chip>
                </td>
              </tr>
              <tr class="border-b">
                <td class="py-2 font-semibold">Oluşturma Tarihi</td>
                <td class="py-2">{{ selectedProduct.CreateDate | date_tr }}</td>
              </tr>
            </table>
          </div>
          <div class="vx-col w-full md:w-1/2">
            <h4 class="mb-4">Ödeme Linki</h4>
            <div class="flex items-center">
              <vs-input class="w-full mr-2" :value="generateUrl(selectedProduct.InvoiceSettingKey)" readonly />
              <vs-button
                color="primary"
                type="border"
                icon-pack="feather"
                icon="icon-copy"
                v-clipboard:copy="generateUrl(selectedProduct.InvoiceSettingKey)"
                v-clipboard:success="onCopy"
              >Kopyala</vs-button>
            </div>

            <h4 class="mt-6 mb-4">Bildirim Ayarları</h4>
            <table class="w-full">
              <tr class="border-b">
                <td class="py-2 font-semibold">E-Posta</td>
                <td class="py-2">{{ selectedProduct.NotifyEmail || '-' }}</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 font-semibold">SMS</td>
                <td class="py-2">{{ selectedProduct.NotifySMS || '-' }}</td>
              </tr>
            </table>

            <h4 class="mt-6 mb-4" v-if="selectedProduct.DiscountRules && selectedProduct.DiscountRules.length > 0">İndirim Kuralları</h4>
            <table class="w-full" v-if="selectedProduct.DiscountRules && selectedProduct.DiscountRules.length > 0">
              <thead>
                <tr class="border-b">
                  <th class="py-2 text-left">İndirim Adı</th>
                  <th class="py-2 text-left">Yüzde</th>
                  <th class="py-2 text-left">Tutar</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(rule, idx) in selectedProduct.DiscountRules" :key="idx" class="border-b">
                  <td class="py-2">{{ rule.DiscountTitle }}</td>
                  <td class="py-2">%{{ rule.DiscountPercent }}</td>
                  <td class="py-2">{{ rule.DiscountPrice | currency }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import vSelect from "vue-select";
import moment from "moment";

import StaffApi from "@/api/staff";
import DataViewSidebar from "../PaymentGroup/DataViewSidebar.vue";
import CellRendererGroupActions from "./cell-renderer/CellRendererGroupActions.vue";

let searchTimeout = null;

export default {
  components: {
    AgGridVue,
    vSelect,
    DataViewSidebar,
    CellRendererGroupActions,
  },
  data() {
    return {
      // Sidebar
      addNewDataSidebar: false,
      sidebarData: {},

      // Filters
      statusFilter: { label: "-tüm durumlar-", value: "" },
      statusOptions: [
        { label: "-tüm durumlar-", value: "" },
        { label: "Aktif", value: "active" },
        { label: "Pasif", value: "passive" },
      ],
      searchQuery: "",

      // Data
      allProducts: [],
      isLoading: false,

      // Pagination
      currentPage: 1,
      pageSize: 20,

      // AgGrid
      gridApi: null,
      gridOptions: {},
      defaultColDef: {
        sortable: true,
        resizable: true,
        suppressMenu: true,
      },
      columnDefs: [
        {
          headerName: "#",
          field: "InvoiceSettingGroupID",
          hide: true,
        },
        {
          headerName: "Hizmet Adı",
          field: "ProductTitle",
          minWidth: 200,
        },
        {
          headerName: "Hizmet Açıklama",
          field: "ProductDesc",
          minWidth: 200,
        },
        {
          headerName: "Hizmet Fiyatı",
          field: "Price",
          maxWidth: 150,
          valueGetter: (params) => {
            return this.$options.filters.currency(params.data.Price);
          },
        },
        {
          headerName: this.$t("Table.PaymentGroup.State"),
          field: "IsActive",
          maxWidth: 120,
          valueGetter: (params) => {
            return params.data.IsActive && !params.data.IsDeleted ? "Aktif" : "Pasif";
          },
          cellStyle: (params) => {
            if (params.value === "Aktif") {
              return { color: "rgba(var(--vs-success), 1)", fontWeight: "600" };
            }
            return { color: "rgba(var(--vs-danger), 1)", fontWeight: "600" };
          },
        },
        {
          headerName: " Tarihi",
          field: "CreateDate",
          sort: "desc",
          maxWidth: 180,
          comparator: function (valueA, valueB, nodeA, nodeB) {
            var dateA = nodeA.data.CreateDate ? new Date(nodeA.data.CreateDate).getTime() : 0;
            var dateB = nodeB.data.CreateDate ? new Date(nodeB.data.CreateDate).getTime() : 0;
            return dateA - dateB;
          },
          valueGetter: function (params) {
            if (!params.data.CreateDate) return "";
            return moment(String(params.data.CreateDate)).format(
              "DD.MM.YYYY HH:mm"
            );
          },
        },
        {
          headerName: this.$t("Table.PaymentGroup.Actions"),
          field: "InvoiceSettingGroupID",
          sortable: false,
          maxWidth: 150,
          cellRendererFramework: "CellRendererGroupActions",
        },
      ],

      components: {
        CellRendererGroupActions,
      },

      // Detail popup
      detailPopup: false,
      detailTitle: "",
      selectedProduct: null,
    };
  },
  watch: {
    statusFilter() {
      this.currentPage = 1;
    },
  },
  computed: {
    filteredData() {
      let data = this.allProducts;

      // Status filter
      if (this.statusFilter.value === "active") {
        data = data.filter((x) => x.IsActive && !x.IsDeleted);
      } else if (this.statusFilter.value === "passive") {
        data = data.filter((x) => !x.IsActive || x.IsDeleted);
      }

      // Search filter
      if (this.searchQuery) {
        const search = this.searchQuery.toLowerCase();
        data = data.filter(
          (x) =>
            (x.ProductTitle && x.ProductTitle.toLowerCase().includes(search)) ||
            (x.ProductDesc && x.ProductDesc.toLowerCase().includes(search)) ||
            (x.NotifyEmail && x.NotifyEmail.toLowerCase().includes(search)) ||
            (x.NotifySMS && x.NotifySMS.toLowerCase().includes(search))
        );
      }

      return data;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredData.length / this.pageSize));
    },
    paginationStart() {
      return Math.min(
        (this.currentPage - 1) * this.pageSize + 1,
        this.filteredData.length
      );
    },
    paginationEnd() {
      return Math.min(
        this.currentPage * this.pageSize,
        this.filteredData.length
      );
    },
  },
  methods: {
    debouncedSearch() {
      if (searchTimeout) clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        this.currentPage = 1;
      }, 400);
    },
    resetFilters() {
      this.$refs.filterCard.removeRefreshAnimation();
      this.statusFilter = { label: "-tüm durumlar-", value: "" };
      this.searchQuery = "";
      this.currentPage = 1;
    },
    toggleDataSidebar(val = false) {
      this.addNewDataSidebar = val;
      if (!val) {
        this.fetchData();
      }
    },
    addNewData() {
      this.sidebarData = {};
      this.toggleDataSidebar(true);
    },
    editData(data) {
      this.sidebarData = data;
      this.toggleDataSidebar(true);
    },
    deleteData(id) {
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: "Dikkat",
        text: "İstenilen hizmet kalıcı olarak silinecek. Devam etmek istiyor musunuz?",
        acceptText: "Sil",
        cancelText: "Vazgeç",
        accept: () => this.removeProduct(id),
      });
    },
    activateData(id) {
      this.$vs.dialog({
        type: "confirm",
        color: "success",
        title: "Dikkat",
        text: "İstenilen hizmet tekrar aktif hale getirilecek. Devam etmek istiyor musunuz?",
        acceptText: "Aktif Et",
        cancelText: "Vazgeç",
        accept: () => this.activeProduct(id),
      });
    },
    async removeProduct(id) {
      this.$vs.loading({ color: "#000", scale: 0.45 });
      let submitData = { InvoiceSettingGroupID: id };
      let response = await StaffApi.removeSettingGroup(submitData, this);
      if (!response.data.error) {
        this.fetchData();
      }
      setTimeout(() => { this.$vs.loading.close(); }, 100);
    },
    async activeProduct(id) {
      this.$vs.loading({ color: "#000", scale: 0.45 });
      let submitData = { InvoiceSettingGroupID: id };
      let response = await StaffApi.activeSettingGroup(submitData, this);
      if (!response.data.error) {
        this.fetchData();
      }
      setTimeout(() => { this.$vs.loading.close(); }, 100);
    },
    generateUrl(key) {
      return process.env.VUE_APP_BASE_URL + "paymentgroup/" + key;
    },
    onCopy() {
      this.$vs.notify({
        title: "Başarılı",
        text: "Link kopyalandı.",
        color: "success",
        iconPack: "feather",
        position: "top-center",
        icon: "icon-check-circle",
      });
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridApi.sizeColumnsToFit();
      window.addEventListener("resize", () => {
        if (this.gridApi) this.gridApi.sizeColumnsToFit();
      });
    },
    getGridLocaleText(key, defaultValue) {
      var gridKey = "Grid_" + key;
      var value = this.$t(gridKey);
      return value === gridKey ? defaultValue : value;
    },
    async fetchData() {
      this.isLoading = true;
      this.$vs.loading({
        color: "#000",
        container: "#paymentGroupsGrid",
        scale: 0.45,
      });

      try {
        let response = await StaffApi.getMyProducts(this);
        if (response.data && !response.data.error) {
          this.allProducts = response.data.model || [];
        }
      } catch (e) {
        console.error("Payment groups fetch error:", e);
      }

      setTimeout(() => {
        this.$vs.loading.close("#paymentGroupsGrid .con-vs-loading");
      }, 100);
      this.isLoading = false;
    },
  },
  beforeMount() {
    this.gridOptions.localeTextFunc = this.getGridLocaleText;
  },
  async mounted() {
    await this.fetchData();
  },
  beforeDestroy() {
    [...document.getElementsByClassName("con-vs-popup")].map(
      (n) => n && n.remove()
    );
  },
};
</script>

<style lang="scss">
#payment-groups-grid-page {
  .ag-grid-cell-chip {
    &.vs-chip-success {
      background: rgba(var(--vs-success), 0.15);
      color: rgba(var(--vs-success), 1) !important;
      font-weight: 500;
    }
    &.vs-chip-danger {
      background: rgba(var(--vs-danger), 0.15);
      color: rgba(var(--vs-danger), 1) !important;
      font-weight: 500;
    }
  }
}
</style>
